import type {
    CommerceImage,
    CommerceMoney,
    CommercePageResult,
    CommercePrice,
    CommerceProduct,
    CommerceProductCategory,
    CommerceProductComponent,
    CommerceProductCardContent,
    CommerceProductDetailSection,
    CommerceProductOptionGroup,
    CommerceProductOptionValue,
    CommercePurchaseOffer,
    CommerceRecurringFrequency,
    CommerceRecurringFrequencyOption,
    CommerceProductStatus,
    CommerceProductSummary,
    CommerceProductVariant,
    CommerceProductVariantSelection,
    GetCommerceProductCategoriesResponse,
    GetCommerceProductCategoryResponse,
    GetCommerceProductResponse,
    SearchCommerceProductsResponse,
} from "@teqtank/commerce-contracts";
import { CommerceError } from "../errors/commerce-error.js";

const PRODUCT_STATUSES = new Set<CommerceProductStatus>([
    "active",
    "unavailable",
    "coming-soon",
    "discontinued",
]);

export function resolveProductSearchResponse(
    response: SearchCommerceProductsResponse | null | undefined,
): SearchCommerceProductsResponse {
    if (response === null || response === undefined || !Array.isArray(response.items)) {
        throw invalidProductResponse("Product search response items were missing.");
    }

    return Object.freeze({
        items: Object.freeze(response.items.map(resolveProductSummary)),

        pagination: resolvePagination(response.pagination),
    });
}

export function resolveProductResponse(
    response: GetCommerceProductResponse | null | undefined,
): CommerceProduct | null {
    if (response === null || response === undefined || typeof response !== "object") {
        throw invalidProductResponse("Product response was missing.");
    }

    if (response.product === null) {
        return null;
    }

    return resolveProduct(response.product);
}

export function resolveProductCategoryResponse(
    response: GetCommerceProductCategoryResponse | null | undefined,
): CommerceProductCategory | null {
    if (response === null || response === undefined || typeof response !== "object") {
        throw invalidProductResponse("Product category response was missing.");
    }

    if (response.category === null) {
        return null;
    }

    return resolveProductCategory(response.category);
}

export function resolveProductCategoriesResponse(
    response: GetCommerceProductCategoriesResponse | null | undefined,
): readonly CommerceProductCategory[] {
    if (response === null || response === undefined || !Array.isArray(response.categories)) {
        throw invalidProductResponse("Product categories response was missing.");
    }

    return Object.freeze(response.categories.map(resolveProductCategory));
}

function resolveProduct(product: CommerceProduct): CommerceProduct {
    const summary = resolveProductSummary(product);
    const optionGroups = requireArray(product.optionGroups, "product.optionGroups").map(
        resolveProductOptionGroup,
    );

    const variants = requireArray(product.variants, "product.variants").map(resolveProductVariant);

    if (variants.length === 0) {
        throw invalidProductResponse("Product variants must not be empty.");
    }

    validateSelectionGraph(optionGroups, variants);

    const defaultVariantId = requireString(product.defaultVariantId, "product.defaultVariantId");

    if (!variants.some((variant) => variant.id === defaultVariantId)) {
        throw invalidProductResponse("Product defaultVariantId did not match a Product variant.");
    }

    return Object.freeze({
        ...summary,

        description: requireString(product.description, "product.description", true),

        images: Object.freeze(requireArray(product.images, "product.images").map(resolveImage)),

        optionGroups: Object.freeze(optionGroups),

        variants: Object.freeze(variants),
        defaultVariantId,

        extensions: Object.freeze({
            ...requireObject(product.extensions, "product.extensions"),
        }),

        ...(product.components == null
            ? {}
            : {
                  components: Object.freeze(
                      requireArray(product.components, "product.components").map(
                          (component, index) =>
                              resolveProductComponent(component, `product.components[${index}]`),
                      ),
                  ),
              }),
        ...(product.detailSections == null
            ? {}
            : {
                  detailSections: Object.freeze(
                      requireArray(product.detailSections, "product.detailSections").map(
                          resolveProductDetailSection,
                      ),
                  ),
              }),
    });
}

function resolveProductSummary(product: CommerceProductSummary): CommerceProductSummary {
    const status = product.status;
    const productType = product.productType ?? "product";

    if (!PRODUCT_STATUSES.has(status)) {
        throw invalidProductResponse(`Unsupported Product status '${String(status)}'.`);
    }
    if (productType !== "product" && productType !== "kit") {
        throw invalidProductResponse("product.productType must be product or kit.");
    }

    return Object.freeze({
        id: requireString(product.id, "product.id"),
        ...(product.displaySku == null
            ? {}
            : {
                  displaySku: requireString(product.displaySku, "product.displaySku"),
              }),
        slug: requireString(product.slug, "product.slug"),
        name: requireString(product.name, "product.name"),

        shortDescription: requireString(product.shortDescription, "product.shortDescription", true),

        status,

        primaryImage: product.primaryImage === null ? null : resolveImage(product.primaryImage),

        representativeOffers: resolvePurchaseOffers(
            product.representativeOffers,
            "product.representativeOffers",
        ),

        categoryIds: Object.freeze(
            requireArray(product.categoryIds, "product.categoryIds").map((value, index) =>
                requireString(value, `product.categoryIds[${index}]`),
            ),
        ),

        tags: Object.freeze(
            requireArray(product.tags, "product.tags").map((value, index) =>
                requireString(value, `product.tags[${index}]`),
            ),
        ),

        productType,

        ...(product.cardContent == null
            ? {}
            : {
                  cardContent: Object.freeze(
                      requireArray(product.cardContent, "product.cardContent").map(
                          resolveProductCardContent,
                      ),
                  ),
              }),

        ...(product.requiresConfiguration === undefined
            ? {}
            : {
                  requiresConfiguration: requireBoolean(
                      product.requiresConfiguration,
                      "product.requiresConfiguration",
                  ),
              }),
    });
}

function resolveProductCardContent(
    content: CommerceProductCardContent,
    index: number,
): CommerceProductCardContent {
    if (content === null || typeof content !== "object") {
        throw invalidProductResponse(`product.cardContent[${index}] must be an object.`);
    }
    if (
        content.format !== undefined &&
        content.format !== "plain-text" &&
        content.format !== "rich-html"
    ) {
        throw invalidProductResponse(`product.cardContent[${index}].format is unsupported.`);
    }
    return Object.freeze({
        id: requireString(content.id, `product.cardContent[${index}].id`),
        ...(content.label == null
            ? {}
            : { label: requireString(content.label, `product.cardContent[${index}].label`) }),
        value: requireString(content.value, `product.cardContent[${index}].value`),
        ...(content.format === undefined ? {} : { format: content.format }),
    });
}

function resolveProductDetailSection(
    section: CommerceProductDetailSection,
    index: number,
): CommerceProductDetailSection {
    if (section === null || typeof section !== "object") {
        throw invalidProductResponse(`product.detailSections[${index}] must be an object.`);
    }
    if (section.display !== "body" && section.display !== "accordion") {
        throw invalidProductResponse(`product.detailSections[${index}].display is unsupported.`);
    }
    if (section.format !== "plain-text" && section.format !== "rich-html") {
        throw invalidProductResponse(`product.detailSections[${index}].format is unsupported.`);
    }
    return Object.freeze({
        id: requireString(section.id, `product.detailSections[${index}].id`),
        ...(section.title == null
            ? {}
            : { title: requireString(section.title, `product.detailSections[${index}].title`) }),
        display: section.display,
        format: section.format,
        content: requireString(section.content, `product.detailSections[${index}].content`),
    });
}

function resolveProductComponent(
    component: CommerceProductComponent,
    property: string,
): CommerceProductComponent {
    return Object.freeze({
        productId: requireString(component.productId, `${property}.productId`),
        ...(component.displaySku == null
            ? {}
            : { displaySku: requireString(component.displaySku, `${property}.displaySku`) }),
        name: requireString(component.name, `${property}.name`),
        image: component.image === null ? null : resolveImage(component.image),
        quantity: requirePositiveInteger(component.quantity, `${property}.quantity`),
        ...(component.components == null
            ? {}
            : {
                  components: Object.freeze(
                      requireArray(component.components, `${property}.components`).map(
                          (child, index) =>
                              resolveProductComponent(child, `${property}.components[${index}]`),
                      ),
                  ),
              }),
    });
}

function resolveProductCategory(category: CommerceProductCategory): CommerceProductCategory {
    const parentId =
        category.parentId === null ? null : requireString(category.parentId, "category.parentId");

    return Object.freeze({
        id: requireString(category.id, "category.id"),
        slug: requireString(category.slug, "category.slug"),
        name: requireString(category.name, "category.name"),

        ...(category.description == null
            ? {}
            : {
                  description: requireString(category.description, "category.description", true),
              }),

        parentId,

        image: category.image === null ? null : resolveImage(category.image),

        ...(category.productCount == null
            ? {}
            : {
                  productCount: requireNonNegativeInteger(
                      category.productCount,
                      "category.productCount",
                  ),
              }),

        sortOrder: requireNonNegativeInteger(category.sortOrder, "category.sortOrder"),
    });
}

function resolvePrice(price: CommercePrice): CommercePrice {
    if (price === null || price === undefined || typeof price !== "object") {
        throw invalidProductResponse("Commerce price was missing.");
    }

    const current = resolveMoney(price.current);
    const compareAt = price.compareAt == null ? undefined : resolveMoney(price.compareAt);

    if (
        compareAt !== undefined &&
        (compareAt.currencyCode !== current.currencyCode || compareAt.amount <= current.amount)
    ) {
        throw invalidProductResponse(
            "Commerce compareAt price must use the current currency and be greater than current.",
        );
    }

    return Object.freeze({ current, ...(compareAt === undefined ? {} : { compareAt }) });
}

function resolvePurchaseOffers(
    offers: readonly CommercePurchaseOffer[] | undefined,
    property: string,
): readonly CommercePurchaseOffer[] {
    const resolved = requireArray(offers, property).map((offer, index) => {
        if (offer === null || offer === undefined || typeof offer !== "object") {
            throw invalidProductResponse(`${property}[${index}] must be an object.`);
        }

        const purchaseType = offer.purchaseType;
        if (purchaseType !== "one-time" && purchaseType !== "recurring") {
            throw invalidProductResponse(`${property}[${index}].purchaseType is unsupported.`);
        }

        const frequency =
            offer.frequency == null
                ? undefined
                : resolveRecurringFrequency(offer.frequency, `${property}[${index}].frequency`);

        if (purchaseType === "one-time" && frequency !== undefined) {
            throw invalidProductResponse("One-time offers cannot declare a recurring frequency.");
        }

        return Object.freeze({
            purchaseType,
            price: resolvePrice(offer.price),
            ...(frequency === undefined ? {} : { frequency }),
        });
    });

    if (new Set(resolved.map((offer) => offer.purchaseType)).size !== resolved.length) {
        throw invalidProductResponse(`${property} contains duplicate purchase types.`);
    }

    return Object.freeze(resolved);
}

function resolveRecurringFrequency(
    frequency: CommerceRecurringFrequency,
    property: string,
): CommerceRecurringFrequency {
    const allowedDays = requireArray(frequency.allowedDays, `${property}.allowedDays`).map(
        (days, index) => requirePositiveInteger(days, `${property}.allowedDays[${index}]`),
    );
    const defaultDays = requirePositiveInteger(frequency.defaultDays, `${property}.defaultDays`);
    const options = requireArray(frequency.options, `${property}.options`).map((option, index) =>
        resolveRecurringFrequencyOption(option, `${property}.options[${index}]`),
    );
    const defaultOptionId =
        frequency.defaultOptionId == null
            ? options.find((option) => option.days === defaultDays)?.id
            : requireString(frequency.defaultOptionId, `${property}.defaultOptionId`);

    if (
        allowedDays.length === 0 ||
        new Set(allowedDays).size !== allowedDays.length ||
        !allowedDays.includes(defaultDays) ||
        options.length !== allowedDays.length ||
        new Set(options.map((option) => option.days)).size !== options.length ||
        new Set(options.map((option) => option.id)).size !== options.length ||
        options.some((option) => !allowedDays.includes(option.days)) ||
        defaultOptionId === undefined ||
        !options.some((option) => option.id === defaultOptionId)
    ) {
        throw invalidProductResponse(
            `${property} requires unique allowedDays including defaultDays and one described option for each allowed day.`,
        );
    }

    return Object.freeze({
        allowedDays: Object.freeze(allowedDays),
        defaultDays,
        options: Object.freeze(options),
        defaultOptionId,
    });
}

export function resolveRecurringFrequencyOption(
    option: CommerceRecurringFrequencyOption,
    property = "frequency",
): CommerceRecurringFrequencyOption {
    if (option === null || typeof option !== "object") {
        throw invalidProductResponse(`${property} is invalid.`);
    }
    const days = requirePositiveInteger(option.days, `${property}.days`);
    const unit = option.unit ?? "day";
    if (unit !== "day" && unit !== "month") {
        throw invalidProductResponse(`${property}.unit must be day or month.`);
    }
    return Object.freeze({
        id: option.id == null ? `day-${days}` : requireString(option.id, `${property}.id`),
        unit,
        interval:
            option.interval == null
                ? days
                : requirePositiveInteger(option.interval, `${property}.interval`),
        days,
        description: requireString(option.description, `${property}.description`),
    });
}

function resolveMoney(money: CommerceMoney): CommerceMoney {
    if (money === null || money === undefined || typeof money !== "object") {
        throw invalidProductResponse("Product money value was missing.");
    }

    if (typeof money.amount !== "number" || !Number.isFinite(money.amount) || money.amount < 0) {
        throw invalidProductResponse(
            "Product money amount must be a finite number greater than or equal to zero.",
        );
    }

    const currencyCode = requireString(money.currencyCode, "money.currencyCode").toUpperCase();

    if (!/^[A-Z]{3}$/u.test(currencyCode)) {
        throw invalidProductResponse("Product money currencyCode must be a valid ISO 4217 code.");
    }

    return Object.freeze({
        amount: money.amount,
        currencyCode,

        formatted: requireString(money.formatted, "money.formatted"),
    });
}

function resolveImage(image: CommerceImage): CommerceImage {
    if (image === null || image === undefined || typeof image !== "object") {
        throw invalidProductResponse("Product image was missing.");
    }

    const width =
        image.width == null ? undefined : requirePositiveInteger(image.width, "image.width");

    const height =
        image.height == null ? undefined : requirePositiveInteger(image.height, "image.height");

    return Object.freeze({
        url: requireString(image.url, "image.url"),

        alternativeText: requireString(image.alternativeText, "image.alternativeText", true),

        ...(width !== undefined
            ? {
                  width,
              }
            : {}),

        ...(height !== undefined
            ? {
                  height,
              }
            : {}),
    });
}

function resolveProductOptionGroup(option: CommerceProductOptionGroup): CommerceProductOptionGroup {
    return Object.freeze({
        id: requireString(option.id, "productOption.id"),
        name: requireString(option.name, "productOption.name"),

        ...(option.dependsOnOptionId == null
            ? {}
            : {
                  dependsOnOptionId: requireString(
                      option.dependsOnOptionId,
                      "productOption.dependsOnOptionId",
                  ),
              }),

        values: Object.freeze(
            requireArray(option.values, "productOption.values").map(resolveProductOptionValue),
        ),
    });
}

function resolveProductOptionValue(value: CommerceProductOptionValue): CommerceProductOptionValue {
    return Object.freeze({
        id: requireString(value.id, "productOptionValue.id"),
        label: requireString(value.label, "productOptionValue.label"),
        ...(value.availableForValueIds == null
            ? {}
            : {
                  availableForValueIds: Object.freeze(
                      requireArray(
                          value.availableForValueIds,
                          "productOptionValue.availableForValueIds",
                      ).map((parentValueId) =>
                          requireString(parentValueId, "productOptionValue.availableForValueIds[]"),
                      ),
                  ),
              }),
    });
}

function resolveProductVariant(variant: CommerceProductVariant): CommerceProductVariant {
    const inventoryQuantity =
        variant.inventoryQuantity == null
            ? undefined
            : requireNonNegativeInteger(
                  variant.inventoryQuantity,
                  "productVariant.inventoryQuantity",
              );

    return Object.freeze({
        id: requireString(variant.id, "productVariant.id"),

        ...(variant.displaySku == null
            ? {}
            : {
                  displaySku: requireString(variant.displaySku, "productVariant.displaySku"),
              }),

        name: requireString(variant.name, "productVariant.name"),

        selections: Object.freeze(
            requireArray(variant.selections, "productVariant.selections").map(
                resolveProductVariantSelection,
            ),
        ),

        offers: resolvePurchaseOffers(variant.offers, "productVariant.offers"),

        available: requireBoolean(variant.available, "productVariant.available"),

        ...(inventoryQuantity !== undefined
            ? {
                  inventoryQuantity,
              }
            : {}),
    });
}

function resolveProductVariantSelection(
    selection: CommerceProductVariantSelection,
): CommerceProductVariantSelection {
    return Object.freeze({
        optionId: requireString(selection.optionId, "productVariantSelection.optionId"),

        valueId: requireString(selection.valueId, "productVariantSelection.valueId"),
    });
}

function validateSelectionGraph(
    optionGroups: readonly CommerceProductOptionGroup[],
    variants: readonly CommerceProductVariant[],
): void {
    const optionIds = new Set<string>();
    const valueIdsByOption = new Map<string, Set<string>>();

    for (const option of optionGroups) {
        if (optionIds.has(option.id)) {
            throw invalidProductResponse(`Duplicate Product option ID '${option.id}'.`);
        }

        if (option.values.length === 0) {
            throw invalidProductResponse(`Product option '${option.id}' has no values.`);
        }

        const valueIds = new Set<string>();
        for (const value of option.values) {
            if (valueIds.has(value.id)) {
                throw invalidProductResponse(
                    `Product option '${option.id}' contains duplicate value '${value.id}'.`,
                );
            }
            valueIds.add(value.id);
        }

        if (option.dependsOnOptionId === undefined) {
            if (option.values.some((value) => value.availableForValueIds !== undefined)) {
                throw invalidProductResponse(
                    `Root Product option '${option.id}' cannot declare controlling values.`,
                );
            }
        } else {
            const controllingValues = valueIdsByOption.get(option.dependsOnOptionId);
            if (controllingValues === undefined) {
                throw invalidProductResponse(
                    `Product option '${option.id}' must depend on an earlier option.`,
                );
            }

            for (const value of option.values) {
                if (
                    value.availableForValueIds === undefined ||
                    value.availableForValueIds.length === 0 ||
                    value.availableForValueIds.some((id) => !controllingValues.has(id))
                ) {
                    throw invalidProductResponse(
                        `Product option '${option.id}' contains invalid controlling values.`,
                    );
                }
            }
        }

        optionIds.add(option.id);
        valueIdsByOption.set(option.id, valueIds);
    }

    const variantIds = new Set<string>();
    const paths = new Set<string>();

    for (const variant of variants) {
        if (variantIds.has(variant.id)) {
            throw invalidProductResponse(`Duplicate Product variant ID '${variant.id}'.`);
        }
        variantIds.add(variant.id);

        const selections = new Map<string, string>();
        for (const selection of variant.selections) {
            if (selections.has(selection.optionId)) {
                throw invalidProductResponse(
                    `Product variant '${variant.id}' selects option '${selection.optionId}' more than once.`,
                );
            }
            selections.set(selection.optionId, selection.valueId);
        }

        const activePath: string[] = [];
        for (const option of optionGroups) {
            const availableValues =
                option.dependsOnOptionId === undefined
                    ? option.values
                    : option.values.filter((value) => {
                          const controllingValue = selections.get(option.dependsOnOptionId!);
                          return (
                              controllingValue !== undefined &&
                              value.availableForValueIds?.includes(controllingValue) === true
                          );
                      });

            const selectedValue = selections.get(option.id);
            if (availableValues.length === 0) {
                if (selectedValue !== undefined) {
                    throw invalidProductResponse(
                        `Product variant '${variant.id}' selects inactive option '${option.id}'.`,
                    );
                }
                continue;
            }

            if (
                selectedValue === undefined ||
                !availableValues.some((value) => value.id === selectedValue)
            ) {
                throw invalidProductResponse(
                    `Product variant '${variant.id}' does not contain a valid selection for active option '${option.id}'.`,
                );
            }

            activePath.push(`${option.id}:${selectedValue}`);
        }

        if (selections.size !== activePath.length) {
            throw invalidProductResponse(
                `Product variant '${variant.id}' contains an unknown option selection.`,
            );
        }

        const signature = activePath.join("|");
        if (paths.has(signature)) {
            throw invalidProductResponse("Product variants contain a duplicate selection path.");
        }
        paths.add(signature);
    }
}

function resolvePagination(pagination: CommercePageResult): CommercePageResult {
    if (pagination === null || pagination === undefined || typeof pagination !== "object") {
        throw invalidProductResponse("Product search pagination was missing.");
    }

    const page = requirePositiveInteger(pagination.page, "pagination.page");

    const pageSize = requirePositiveInteger(pagination.pageSize, "pagination.pageSize");

    const totalItems = requireNonNegativeInteger(pagination.totalItems, "pagination.totalItems");

    const totalPages = requireNonNegativeInteger(pagination.totalPages, "pagination.totalPages");

    return Object.freeze({
        page,
        pageSize,
        totalItems,
        totalPages,

        hasPreviousPage: requireBoolean(pagination.hasPreviousPage, "pagination.hasPreviousPage"),

        hasNextPage: requireBoolean(pagination.hasNextPage, "pagination.hasNextPage"),
    });
}

function requireString(value: unknown, property: string, allowEmpty = false): string {
    if (typeof value !== "string") {
        throw invalidProductResponse(`${property} must be a string.`);
    }

    const normalized = value.trim();

    if (!allowEmpty && normalized.length === 0) {
        throw invalidProductResponse(`${property} must not be empty.`);
    }

    return normalized;
}

function requireBoolean(value: unknown, property: string): boolean {
    if (typeof value !== "boolean") {
        throw invalidProductResponse(`${property} must be a boolean.`);
    }

    return value;
}

function requireArray<T>(value: readonly T[] | undefined, property: string): readonly T[] {
    if (!Array.isArray(value)) {
        throw invalidProductResponse(`${property} must be an array.`);
    }

    return value;
}

function requireObject(
    value: Readonly<Record<string, string>> | undefined,
    property: string,
): Readonly<Record<string, string>> {
    if (
        value === null ||
        value === undefined ||
        typeof value !== "object" ||
        Array.isArray(value)
    ) {
        throw invalidProductResponse(`${property} must be an object.`);
    }

    for (const [key, item] of Object.entries(value)) {
        if (typeof item !== "string") {
            throw invalidProductResponse(`${property}.${key} must be a string.`);
        }
    }

    return value;
}

function requirePositiveInteger(value: unknown, property: string): number {
    if (!Number.isInteger(value) || Number(value) < 1) {
        throw invalidProductResponse(`${property} must be an integer greater than zero.`);
    }

    return Number(value);
}

function requireNonNegativeInteger(value: unknown, property: string): number {
    if (!Number.isInteger(value) || Number(value) < 0) {
        throw invalidProductResponse(
            `${property} must be an integer greater than or equal to zero.`,
        );
    }

    return Number(value);
}

function invalidProductResponse(message: string): CommerceError {
    return new CommerceError("product-response-invalid", message);
}
